import React, { useEffect, useRef, useState, useCallback, useMemo, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch, batch } from 'react-redux';
import * as actions from '../../../../store/index';
import {
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  Checkbox,
} from '@material-ui/core';
import { setPagination, setSort, setScrollPosition } from '../../../../store';
import { fetchPost } from '../../../../helpers/fetching';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import TablePlaceholder from './ExaminationTable/Helpers/TablePlaceholder';
import DropzoneAreaTable from './ExaminationTable/Helpers/DropzoneAreaTable';
import ExaminationTableRows from './ExaminationTable/ExaminationTableRows';
import { useStyles } from './ExaminationTable/ExaminationTable.style';
import { getExamTableColumnWidths, getColumnCellStyle, getTableTransitionStyle, BULK_CHECKBOX_WIDTH } from './examTableColumnWidths';

const compareExams = (a, b, orderBy, order) => {
  if (a.is_prior !== b.is_prior) return a.is_prior ? 1 : -1;

  const direction = order === 'ASC' ? 1 : -1;

  const getValue = (exam) => {
    if (orderBy === 'date') return exam.date ? new Date(exam.date).getTime() : 0;
    if (orderBy === 'date_uploaded') return exam.date_uploaded ? new Date(exam.date_uploaded).getTime() : 0;
    if (orderBy === 'name') return (exam.name || '').toLowerCase();
    if (orderBy === 'patient_id') return String(exam.patient_id || '').toLowerCase();
    return exam.date ? new Date(exam.date).getTime() : 0;
  };

  const aVal = getValue(a);
  const bVal = getValue(b);
  if (aVal < bVal) return -1 * direction;
  if (aVal > bVal) return 1 * direction;
  return 0;
};

function ExaminationsListTable(props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const tableEl = props.tableEl;
  const classes = useStyles();

  const [rows, setRows] = useState([]);
  const [fileObjects, setFileObjects] = useState([]);
  const [isDropAreaVisible, setDropAreaVisible] = useState(false);
  const [uploadingProgress, setUploadingProgress] = useState(100);
  const [canFetchMore, setCanFetchMore] = useState(true);
  const [hasScrollListener, setHasScrollListener] = useState(false);

  const {
    exam_list,
    priors_list,
    fetchingExamList,
    currentlyProcessed,
    pagination,
    totalExaminations,
    order,
    orderBy,
    currentExam,
    scrollPosition,
    bulkAction,
    bulkExaminationsSelected,
  } = useSelector((state) => state.examinations);
  const dates = useSelector((state) => state.dates);
  const filter = useSelector((state) => state.filter);
  const premium_features = useSelector((store) => store.global_settings.premium_features);

  const examinationListDrawer = useSelector((state) => state.gui.examinationListDrawer);

  const risk_module = premium_features?.risk_module ?? false;

  const visibleExamIds = useMemo(
    () => rows.flatMap((row) => row.filter((item) => !item.is_prior).map((item) => item.id)).filter(Boolean),
    [rows],
  );

  const allVisibleSelected =
    visibleExamIds.length > 0 && visibleExamIds.every((examId) => bulkExaminationsSelected.includes(examId));
  const someVisibleSelected = visibleExamIds.some((examId) => bulkExaminationsSelected.includes(examId));

  const handleSelectAllPatients = (event) => {
    const shouldSelectAll = event.target.checked;

    if (shouldSelectAll) {
      const merged = [...new Set([...bulkExaminationsSelected, ...visibleExamIds])];
      dispatch(actions.setBulkExaminationsSelected(merged));
      return;
    }

    const remaining = bulkExaminationsSelected.filter((id) => !visibleExamIds.includes(id));
    dispatch(actions.setBulkExaminationsSelected(remaining));
  };

  const columnWidths = useMemo(
    () =>
      getExamTableColumnWidths(props.expandedPatientList, props.drawerWidth, {
        bulkAction,
      }),
    [props.expandedPatientList, props.drawerWidth, bulkAction],
  );

  const tableColumns = useMemo(
    () => [
      { key: 'expand', width: columnWidths.expand },
      { key: 'name', width: columnWidths.name },
      ...(props.expandedPatientList
        ? [
            { key: 'dob', width: columnWidths.dob },
            { key: 'patientId', width: columnWidths.patientId },
          ]
        : []),
      { key: 'icons', width: columnWidths.icons },
      { key: 'date', width: columnWidths.date },
      { key: 'risk', width: columnWidths.risk },
      { key: 'actions', width: columnWidths.actions },
    ],
    [columnWidths],
  );

  const tableHeader = useMemo(
    () => [
      { key: 'expand', label: '', width: columnWidths.expand, padding: 'none' },
      {
        name: 'name',
        label: t('Table.name'),
        align: 'left',
        padding: 'none',
        sort: true,
        width: columnWidths.name,
      },

      ...(props.expandedPatientList
        ? [
            {
              name: 'dob',
              label: t('Table.dob'),
              align: 'left',
              padding: 'none',
              sort: true,
              width: columnWidths.dob,
            },
            {
              name: 'patient_id',
              label: t('Table.patient_id'),
              align: 'left',
              padding: 'none',
              sort: true,
              width: columnWidths.patientId,
            },
          ]
        : []),

      {
        name: 'icons',
        label: '',
        align: 'center',
        padding: 'none',
        sort: false,
        width: columnWidths.icons,
      },

      {
        name: 'date',
        label: t('Table.date'),
        align: 'left',
        padding: 'none',
        sort: true,
        width: columnWidths.date,
      },

      {
        name: 'risk',
        label: risk_module ? t('Table.risk') : '',
        align: 'right',
        padding: 'none',
        sort: false,
        width: columnWidths.risk,
      },

      {
        name: 'actions',
        label: '',
        align: 'left',
        padding: 'none',
        sort: false,
        width: columnWidths.actions,
      },
    ],
    [columnWidths, props.expandedPatientList],
  );

  const scrollListener = useCallback(() => {
    const tableRef = tableEl?.current;
    const bottomPage = tableEl.current.scrollHeight - tableEl.current.clientHeight;
    const bottomReach = bottomPage - tableEl.current.scrollTop;

    if (!canFetchMore || fetchingExamList) {
      tableRef?.removeEventListener('scroll', scrollListener);
      return;
    }

    if (bottomReach < 5 && exam_list.length < totalExaminations && canFetchMore) {
      setCanFetchMore(false);
      dispatch(setPagination([pagination[0], pagination[1] + 30]));
      setHasScrollListener(true);
    }
  }, [exam_list, pagination[1], fetchingExamList, totalExaminations, canFetchMore, dispatch]);

  useLayoutEffect(() => {
    if (tableEl.current) {
      const tableRef = tableEl.current;
      tableRef.addEventListener('scroll', scrollListener);

      return () => {
        tableRef.removeEventListener('scroll', scrollListener);
      };
    }
  }, [scrollListener, hasScrollListener]);

  const prepareData = useCallback(() => {
    const examSet = new Map();
    const addedPriors = new Set();

    for (let i = 0; i < exam_list.length; i++) {
      const exam = exam_list[i];

      const priorsList = priors_list.filter((prior) => prior.patient_id === exam.patient_id);

      if (examSet.has(exam.patient_id)) {
        examSet.get(exam.patient_id).push(exam);
      } else {
        examSet.set(exam.patient_id, [{ ...exam, priors_found: !!priorsList && priorsList.length > 0 }]);
      }

      if (!!priorsList && priorsList.length > 0 && !addedPriors.has(exam.patient_id)) {
        addedPriors.add(exam.patient_id);
        for (let j = 0; j < priorsList.length; j++) {
          examSet.get(exam.patient_id).push({
            ...priorsList[j],
            is_prior: true,
          });
        }
      }
    }
    const sortedGroups = Array.from(examSet.values()).map((group) =>
      [...group].sort((a, b) => compareExams(a, b, orderBy, order)),
    );

    sortedGroups.sort((a, b) => {
      const aRep = a.find((exam) => !exam.is_prior) || a[0];
      const bRep = b.find((exam) => !exam.is_prior) || b[0];
      return compareExams(aRep, bRep, orderBy, order);
    });

    return sortedGroups;
  }, [exam_list, priors_list, order, orderBy]);

  useEffect(() => {
    if (examinationListDrawer) {
      if (tableEl.current) {
        const currentTable = tableEl.current;
        const timer = setTimeout(() => {
          if (currentExam) {
            if (currentTable.scrollTop !== undefined) {
              currentTable.scrollTop = scrollPosition || 0;
            }
          } else {
            if (currentTable.scrollTop !== undefined) {
              currentTable.scrollTop = 0;
            }
          }
        }, 1000);

        return () => clearTimeout(timer);
      }
    }
  }, [examinationListDrawer]);

  useEffect(() => {
    const preparedRows = prepareData();
    setRows(preparedRows);
    if (!fetchingExamList) {
      setCanFetchMore(true);
      if (tableEl.current && hasScrollListener && canFetchMore && exam_list.length < totalExaminations) {
        const bottomPage = tableEl.current.scrollHeight - tableEl.current.clientHeight;
        const scrollPosition = bottomPage - 120;
        tableEl.current.scrollTop = scrollPosition;
        dispatch(setScrollPosition(scrollPosition));
      }
    }
  }, [exam_list, prepareData, fetchingExamList, canFetchMore, hasScrollListener, totalExaminations]);

  useEffect(() => {
    setDropAreaVisible(fileObjects.length > 0);
  }, [fileObjects]);

  const createSortHandler = (columnName) => () => {
    let newOrderBy = columnName;
    let newOrder = 'DESC';

    if (columnName === 'date') {
      if (orderBy === 'date' && order === 'DESC') {
        newOrder = 'ASC';
      } else if (orderBy === 'date' && order === 'ASC') {
        newOrderBy = 'date_uploaded';
        newOrder = 'DESC';
      } else {
        newOrderBy = 'date';
        newOrder = 'DESC';
      }
    } else {
      const isDesc = orderBy === columnName && order === 'DESC';
      newOrder = isDesc ? 'ASC' : 'DESC';
    }

    batch(() => {
      dispatch(setSort({ order: newOrder, orderBy: newOrderBy }));
      dispatch(setPagination([0, 40]));
    });
  };

  const sendToOrthanc = async () => {
    if (!fileObjects.length) return;
    if (uploadingProgress !== 100) return;

    setUploadingProgress(0);
    for (let i = 0; i < fileObjects.length; i++) {
      const response = await fetchPost('dicoms', fileObjects[i].data.split(',')[1], 'bbox');
      setUploadingProgress(((i + 1) / fileObjects.length) * 100);
      const message = response.success
        ? `${i + 1} / ${fileObjects.length} ${t('Upload.snack_uploaded')}`
        : t(`api_messages.${response.msg}`);
      dispatch(
        actions.setSnackbar({
          msg: message,
          severity: response.success ? 'success' : 'error',
        }),
      );
    }
    setFileObjects([]);
  };
  const sortInfo = (head, currentRow) => {
    const headTitle = head === 'name' ? t('DatePicker.name') : t('DatePicker.date');
    const orderText = order === 'ASC' ? t('DatePicker.asc') : t('DatePicker.des');

    if (currentRow && head === orderBy) {
      return t('DatePicker.sorted_in', {
        column: headTitle,
        sorted: orderText,
      });
    } else {
      return head === 'name' ? t('DatePicker.no_sort') : t('DatePicker.upload_date');
    }
  };

  return (
    <>
      <TableContainer
        onDragEnter={() => setDropAreaVisible(true)}
        style={{
          display: isDropAreaVisible && 'none',
          overflowX: 'hidden',
          height: '100%',
          maxWidth: '100%',
          width: '100%',
        }}
        ref={tableEl}>
        {rows.length || exam_list.length > 0 ? (
          <Table
            stickyHeader
            aria-label='collapsible-table'
            style={getTableTransitionStyle(props.drawerWidth)}>
            <colgroup>
              {bulkAction && <col style={{ width: columnWidths.bulk || `${BULK_CHECKBOX_WIDTH}px` }} />}
              {tableColumns.map((column) => (
                <col key={column.key} style={{ width: column.width }} />
              ))}
            </colgroup>
            <TableHead>
              <TableRow>
                {bulkAction && (
                  <TableCell
                    className={classes.tr_padding}
                    style={getColumnCellStyle(columnWidths.bulk || `${BULK_CHECKBOX_WIDTH}px`, {
                      noPadding: true,
                    })}>
                    <Tooltip
                      title={
                        <Typography className={classes.tooltip_texts}>
                          {allVisibleSelected
                            ? t('Table.select_none')
                            : t('Table.select_all', { total: visibleExamIds?.length || 0 })}
                        </Typography>
                      }
                      arrow
                      placement='top'>
                      <Checkbox
                        className={classes.bulkCheckbox}
                        size='small'
                        color='primary'
                        checked={allVisibleSelected}
                        indeterminate={!allVisibleSelected && someVisibleSelected}
                        onChange={handleSelectAllPatients}
                        inputProps={{ 'aria-label': 'select all patients' }}
                        style={{ width: '14px', height: '14px', margin: '2px' }}
                      />
                    </Tooltip>
                  </TableCell>
                )}

                {/* <TableCell style={{ width: '24px', padding: '2px' }} /> */}

                {tableHeader.map((head, i) => (
                  <TableCell
                    key={head.label + i}
                    align={head.align}
                    padding={head.padding}
                    sortDirection={orderBy === head.name ? order.toLowerCase() : false}
                    style={getColumnCellStyle(head.width, { noPadding: head.key === 'expand' })}>
                    {head.sort ? (
                      <Tooltip
                        arrow
                        placement='top'
                        title={
                          <Typography
                            style={{
                              letterSpacing: '0.1rem',
                              fontSize: '12px',
                            }}>
                            {sortInfo(head.name, orderBy === head.name)}
                          </Typography>
                        }>
                        <TableSortLabel
                          active={orderBy === head.name}
                          direction={orderBy === head.name ? order.toLowerCase() : 'asc'}
                          onClick={createSortHandler(head.name)}>
                          {head.label}
                        </TableSortLabel>
                      </Tooltip>
                    ) : (
                      head.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <ExaminationTableRows
              rows={rows}
              setCanFetchMore={setCanFetchMore}
              currentTableRow={tableEl?.current?.scrollTop}
              expandedPatientList={props.expandedPatientList}
              columnWidths={columnWidths}
            />
          </Table>
        ) : (
          <TablePlaceholder colSpan={6} loading={fetchingExamList || currentlyProcessed} centered>
            <Typography className={classes.table_placeholder}>{t('DatePicker.resetFilter')}</Typography>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-evenly',
                flexDirection: 'column',
              }}>
              <Typography className={classes.table_drag_placeholder}>
                {t('DatePicker.drag_drop')}
                <CloudUploadIcon fontSize='inherit' className={classes.drag_placeholder_icon} />
              </Typography>
            </div>
          </TablePlaceholder>
        )}
      </TableContainer>

      {isDropAreaVisible && (
        <DropzoneAreaTable
          fileObjects={fileObjects}
          setFileObjects={setFileObjects}
          setDropAreaVisible={setDropAreaVisible}
          sendToOrthanc={sendToOrthanc}
        />
      )}
    </>
  );
}

export default ExaminationsListTable;
